import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, FileCheck2, Landmark, Rocket, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/project/components/site-shell";
import { supabase } from "@/project/integrations/supabase/client";
import { initiatives } from "@/project/data/sample";
import { platformFee } from "@/project/data/network";

export const Route = createFileRoute("/partner-apply")({
  head: () => ({
    meta: [
      { title: "Join as a Partner Organisation — Daan Amrut Foundation" },
      { name: "description", content: "Panjrapols, gaushalas, NGOs and foundations can apply to raise funds on the Daan Amrut platform with your own admin console and 80G receipts." },
      { property: "og:title", content: "Join as a Partner Organisation — Daan Amrut Foundation" },
      { property: "og:description", content: "Apply to raise funds on Daan Amrut with your own admin console and instant 80G receipts." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PartnerApply,
});

const steps = [
  { icon: FileCheck2, title: "Apply", body: "Share organisation details, registration number and the domain you work in." },
  { icon: ShieldCheck, title: "Verification", body: "We check 12A/80G, trust deed, audited accounts and do a site visit." },
  { icon: Landmark, title: "Bank & KYC", body: "Name-matched bank account and authorised signatory KYC are recorded." },
  { icon: Rocket, title: "Go live", body: "Your public profile, admin console and first campaign go live within 10 days." },
];

export function AppliedNotice() {
  return (
    <div className="rounded-2xl border border-[var(--success)]/40 bg-[oklch(0.96_0.04_155)] p-6 text-center">
      <CheckCircle2 className="mx-auto h-8 w-8 text-[var(--success)]" />
      <h2 className="mt-3 font-display text-xl font-bold">Application received</h2>
      <p className="mt-2 text-sm text-foreground/70">
        Our partnerships team will reach out within 3 working days with the document checklist and a site-visit slot.
      </p>
    </div>
  );
}

function PartnerApply() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const v = (k: string) => (fd.get(k) as string | null)?.trim() || null;
    setBusy(true);
    try {
      const { error } = await supabase.from("partner_applications").insert({
        org_name: v("org_name")!,
        org_type: v("org_type"),
        domain: v("domain"),
        city: v("city"),
        state: v("state"),
        contact_name: v("contact_name")!,
        contact_email: v("contact_email")!,
        contact_phone: v("contact_phone"),
        registration_no: v("registration_no"),
        reg_80g: v("reg_80g"),
        about: v("about"),
      });
      if (error) throw error;
      setSent(true);
      toast.success("Application submitted — our partnerships team will be in touch.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit your application.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <SiteShell>
      <section className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">For organisations</p>
          <h1 className="mt-2 max-w-3xl font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Raise under the Daan Amrut roof — with your own admin console
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground/70 sm:text-base">
            Panjrapols, gaushalas, NGOs and associated foundations join the network, run their own campaigns and standard
            giving programmes, manage donors, and receive twice-monthly settlements. We handle payments, 80G receipts,
            compliance and donor trust.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-border/60 bg-card p-5">
              <s.icon className="h-5 w-5 text-[var(--saffron-deep)]" />
              <p className="mt-3 text-xs uppercase tracking-wider text-foreground/70">Step {i + 1}</p>
              <p className="font-display text-lg font-semibold">{s.title}</p>
              <p className="mt-1 text-sm text-foreground/65">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 pb-16 sm:px-6 lg:grid-cols-[1fr_340px]">
        <div className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
          {sent ? (
            <AppliedNotice />
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <h2 className="font-display text-xl font-bold">Partner application</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Organisation name" name="org_name" required />
                <Field label="Organisation type" name="org_type" placeholder="Panjrapol / Gaushala / NGO / Foundation" required />
                <label className="block">
                  <span className="text-xs text-foreground/60">Primary giving domain</span>
                  <select name="domain" required className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm">
                    {initiatives.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
                  </select>
                </label>
                <Field label="Registration number (12A / trust)" name="registration_no" required />
                <Field label="City" name="city" required />
                <Field label="State" name="state" required />
                <Field label="Authorised person" name="contact_name" required />
                <Field label="Designation" name="designation" />
                <Field label="Phone" name="contact_phone" type="tel" required />
                <Field label="Email" name="contact_email" type="email" required />
                <Field label="PAN of organisation" name="pan" />
                <Field label="80G certificate number" name="reg_80g" />
              </div>
              <label className="block">
                <span className="text-xs text-foreground/60">Tell us about your work</span>
                <textarea name="about" rows={4} required maxLength={1200} className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm" />
              </label>
              <label className="flex items-start gap-2 text-sm text-foreground/70">
                <input type="checkbox" required className="mt-1 h-4 w-4 accent-[var(--maroon)]" />
                <span>I confirm the details are accurate and agree to Daan Amrut's verification, reporting and settlement terms.</span>
              </label>
              <button type="submit" disabled={busy} className="rounded-full bg-[var(--maroon)] px-6 py-3 text-sm font-semibold text-[var(--cream)] disabled:opacity-60">
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </form>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-border/60 bg-[var(--cream-2)] p-6">
            <h3 className="font-display text-lg font-bold">What you get</h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground/75">
              <li>· Your own public profile page and donation links</li>
              <li>· A dedicated admin console for campaigns, donors and reports</li>
              <li>· Instant 80G receipts issued in the foundation's name</li>
              <li>· Twice-monthly settlements with UTR-level statements</li>
              <li>· Donor CRM, impact posting and compliance reminders</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <h3 className="font-display text-lg font-bold">Platform fee</h3>
            <p className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon)]">{platformFee.standard}%</p>
            <p className="mt-1 text-sm text-foreground/65">{platformFee.note}</p>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <h3 className="font-display text-lg font-bold">Documents to keep ready</h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground/75">
              <li>· Trust deed / registration certificate</li>
              <li>· 12A and 80G certificates</li>
              <li>· PAN of the organisation</li>
              <li>· Last two years' audited accounts</li>
              <li>· Cancelled cheque of the trust account</li>
              <li>· Photo ID of the authorised signatory</li>
            </ul>
          </div>
        </aside>
      </section>
    </SiteShell>
  );
}

function Field({ label, name, type = "text", required, placeholder }: { label: string; name?: string; type?: string; required?: boolean; placeholder?: string }) {
  return (
    <label className="block">
      <span className="text-xs text-foreground/60">{label}</span>
      <input name={name} type={type} required={required} placeholder={placeholder} maxLength={120} className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm" />
    </label>
  );
}
